import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { products, agedProducts, smsProducts, smsHomeownerProducts } from '../data/products';
import { ArrowRight } from 'lucide-react';

const LeadCatalog: React.FC = () => {
  const navigate = useNavigate();

  // Group products by category with safety checks
  const freshLeads = React.useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    return products.filter(p => p && !p.soldOut && p.category !== 'Dialer Technology');
  }, []);
  
  const dialerProducts = React.useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    return products.filter(p => p && !p.soldOut && p.category === 'Dialer Technology');
  }, []);
  
  const handleLoginClick = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => navigate('/agent/login'), 300);
  };

  const smsLeads = React.useMemo(() => {
    if (!smsProducts || !Array.isArray(smsProducts)) return [];
    return smsProducts.filter(p => p && !p.soldOut);
  }, []);

  const smsHomeownerLeads = React.useMemo(() => {
    if (!smsHomeownerProducts || !Array.isArray(smsHomeownerProducts)) return [];
    return smsHomeownerProducts.filter(p => p && !p.soldOut);
  }, []);

  // Loading/error state
  if (!products || !agedProducts) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-slate-400">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Compact Header */}
      <section className="pt-24 pb-8 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
                Lead Catalog
              </h1>
              <p className="text-slate-600">
                What we sell. Login to see pricing and order.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={handleLoginClick}
                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base bg-navy-700 text-white font-semibold rounded-lg hover:bg-navy-800 transition-colors"
              >
                Agent Login
              </button>
              <Link
                to="/apply"
                className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base text-center bg-white text-slate-700 font-semibold rounded-lg border border-slate-300 hover:border-slate-400 transition-colors"
              >
                Become an Agent
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-4 sm:py-6 bg-slate-100 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Mobile: Grid */}
          <div className="grid grid-cols-2 gap-3 sm:hidden">
            <div className="text-center p-2 bg-white rounded-lg">
              <div className="text-lg font-bold text-slate-900">14</div>
              <div className="text-xs text-slate-500">A.I. Qualified Leads</div>
            </div>
            <div className="text-center p-2 bg-white rounded-lg">
              <div className="text-lg font-bold text-slate-900">3</div>
              <div className="text-xs text-slate-500">Direct To Consumer Leads</div>
            </div>
            <div className="text-center p-2 bg-white rounded-lg">
              <div className="text-lg font-bold text-slate-900">4</div>
              <div className="text-xs text-slate-500">Dialer Options</div>
            </div>
            <div className="text-center p-2 bg-white rounded-lg">
              <div className="text-lg font-bold text-slate-900">4</div>
              <div className="text-xs text-slate-500">Aged Lead Types</div>
            </div>
            <div className="text-center p-2 bg-white rounded-lg">
              <div className="text-lg font-bold text-slate-900">3</div>
              <div className="text-xs text-slate-500">Inbound / Live Transfer</div>
            </div>
            <div className="text-center p-2 bg-white rounded-lg">
              <div className="text-lg font-bold text-slate-900">24hr Or Less</div>
              <div className="text-xs text-slate-500">Delivery Time</div>
            </div>
          </div>
          {/* Desktop: Inline */}
          <div className="hidden sm:flex flex-wrap items-center justify-center gap-6 text-sm">
            <span className="text-slate-600"><strong className="text-slate-900">14</strong> A.I. Qualified Leads</span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-600"><strong className="text-slate-900">3</strong> Direct To Consumer Leads</span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-600"><strong className="text-slate-900">4</strong> Dialer Options</span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-600"><strong className="text-slate-900">4</strong> Aged Lead Types</span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-600"><strong className="text-slate-900">3</strong> Inbound / Live Transfer</span>
            <span className="text-slate-300">•</span>
            <span className="text-slate-600"><strong className="text-slate-900">24hr Or Less</strong> Delivery Time</span>
          </div>
        </div>
      </section>

      {/* SMS Leads */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">SMS Leads</h2>
            <p className="text-slate-500 mt-1">Leads who confirmed their interest via SMS response. Warm prospects ready for immediate contact.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {smsLeads.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-slate-200 rounded-lg p-5 hover:border-violet-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-medium text-violet-600 uppercase tracking-wide">
                    {product.category}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                  {product.shortDescription}
                </p>
                <button
                  onClick={handleLoginClick}
                  className="text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors"
                >
                  Login to see pricing →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SMS Homeowner Leads */}
      <section className="py-12 bg-slate-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">SMS Homeowner Leads</h2>
            <p className="text-slate-500 mt-1">A.I.-qualified homeowner life leads confirmed via SMS. Engaged, screened, and verified before they reach you.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {smsHomeownerLeads.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-slate-200 rounded-lg p-5 hover:border-emerald-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">
                    {product.category}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                  {product.shortDescription}
                </p>
                <button
                  onClick={handleLoginClick}
                  className="text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors"
                >
                  Login to see pricing →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dialer Products */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Dialer Seats</h2>
            <p className="text-slate-500 mt-1">Power dialer access with leads included. This is how you 3x your call volume.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {dialerProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-slate-200 rounded-lg p-5 hover:border-purple-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">
                    {product.category}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                  {product.shortDescription}
                </p>
                <button
                  onClick={handleLoginClick}
                  className="text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors"
                >
                  Login to see pricing →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Aged Leads */}
      <section className="py-12 bg-slate-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Aged Leads</h2>
            <p className="text-slate-500 mt-1">Older leads at a fraction of the cost. Great for high-volume dialers who want to maximize ROI.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {agedProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-slate-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">
                    {product.category}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                  {product.shortDescription}
                </p>
                <button
                  onClick={handleLoginClick}
                  className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                >
                  Login to see pricing →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fresh Leads */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Fresh Leads</h2>
            <p className="text-slate-500 mt-1">New leads delivered same-day or next-day. These are people who just filled out a form.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {freshLeads.map((product) => (
              <div
                key={product.id}
                className="bg-white border border-slate-200 rounded-lg p-5 hover:border-slate-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                    {product.category}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  {product.name}
                </h3>
                <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                  {product.shortDescription}
                </p>
                <button
                  onClick={handleLoginClick}
                  className="text-sm font-medium text-navy-700 hover:text-navy-800 transition-colors"
                >
                  Login to see pricing →
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-navy-900">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Ready to order?
          </h2>
          <p className="text-navy-200 mb-8 max-w-xl mx-auto">
            Login to access pricing, place orders, and get leads delivered to your inbox. 
            Not an agent yet? Apply and get set up in 24 hours.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleLoginClick}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-navy-800 font-semibold rounded-lg hover:bg-slate-100 transition-colors"
            >
              Login to Store
              <ArrowRight className="w-5 h-5" />
            </button>
            <Link
              to="/apply"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white font-semibold rounded-lg border border-white/30 hover:bg-white/10 transition-colors"
            >
              Become an Agent
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LeadCatalog;
